import * as i2$1 from '@ngrx/store';
import { createAction, props, createReducer, on, createFeatureSelector, createSelector, Store, StoreModule } from '@ngrx/store';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Optional, ChangeDetectorRef, TemplateRef, ViewContainerRef, Directive, Input, APP_INITIALIZER, NgModule } from '@angular/core';
import * as i3 from '@angular/router';
import * as i6 from '@domgen/dgx-fe-common';
import { mapToLatestFrom, filterNullUndefined, DOMAIN_ID, WINDOW, BYPASS_ROUTE_GUARD, DomainId, RequestAction, mockTrustpilotConfig, RequestSource } from '@domgen/dgx-fe-common';
import * as i2 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import { of, from, isObservable, BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { map, mergeMap, reduce, filter, switchMap, catchError, tap, distinctUntilChanged, first } from 'rxjs/operators';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i1 from '@domgen/dgx-fe-logging';
import { LoggingService, DgxLoggingModule } from '@domgen/dgx-fe-logging';
import { __decorate, __metadata } from 'tslib';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';

const init = createAction('[Config] Init');
const getConfig = createAction('[Config] Get Config', props());
const getConfigSuccess = createAction('[Config] Get Config: Success', props());
const getConfigFailure = createAction('[Config] Get Config: Failure', props());

var config_actions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    init: init,
    getConfig: getConfig,
    getConfigSuccess: getConfigSuccess,
    getConfigFailure: getConfigFailure
});

const CONFIG_ADAPTER = new InjectionToken('Config Adapter');

const DOMAIN_CONFIG = new InjectionToken('Client Config');

const ENVIRONMENT = new InjectionToken('Environment');

const RUNTIME_CONFIG = new InjectionToken('Runtime Config');

class ConfigService {
    constructor(domainConfig, environment, runtimeConfig, configAdapters) {
        this.domainConfig = domainConfig;
        this.environment = environment;
        this.runtimeConfig = runtimeConfig;
        this.configAdapters = configAdapters;
    }
    getConfig() {
        const config = this.defaultConfig;
        return this.evaluateConfigAdapters(config, this.configAdapters);
    }
    get defaultConfig() {
        return {
            ...this.environment,
            ...this.runtimeConfig,
            ...this.domainConfig,
            features: {
                ...this.environment.buildFeatures,
                ...this.runtimeConfig.runtimeFeatures,
                ...this.domainConfig.domainFeatures,
            },
        };
    }
    evaluateConfigAdapters(config, configAdapters) {
        if (!configAdapters) {
            return of(config);
        }
        return from(configAdapters).pipe(map((configAdapter) => configAdapter.getConfig()), mergeMap((adaptedConfig) => isObservable(adaptedConfig) ? adaptedConfig : of(adaptedConfig)), reduce((prevConfig, adaptedConfig) => ({
            ...prevConfig,
            ...adaptedConfig,
        }), config));
    }
}
ConfigService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ConfigService, deps: [{ token: DOMAIN_CONFIG }, { token: ENVIRONMENT }, { token: RUNTIME_CONFIG }, { token: CONFIG_ADAPTER, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
ConfigService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ConfigService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOMAIN_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ENVIRONMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [RUNTIME_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [CONFIG_ADAPTER]
                }] }]; } });

class ThemeService {
    constructor(document, loggingService) {
        this.document = document;
        this.loggingService = loggingService;
    }
    applyDomainTheme(themeCssClass) {
        this.loggingService.debug('ThemeService', 'Apply client theme', themeCssClass);
        this.document.body.classList.add(themeCssClass);
    }
}
ThemeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ThemeService, deps: [{ token: DOCUMENT }, { token: i1.LoggingService }], target: i0.ɵɵFactoryTarget.Injectable });
ThemeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ThemeService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ThemeService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i1.LoggingService }]; } });

const CONFIG_FEATURE_KEY = 'config';
const initialState = {
    config: undefined,
    configLoaded: false,
    configLoading: false,
};
const configReducer = createReducer(initialState, on(init, (state) => ({
    ...state,
    configLoading: false,
})), on(getConfig, (state, action) => ({
    ...state,
    domainId: action.domainId,
    configLoaded: false,
    configLoading: true,
})), on(getConfigSuccess, (state, action) => ({
    ...state,
    domainId: action.domainId,
    config: action.config,
    configLoaded: true,
    configLoading: false,
})), on(getConfigFailure, (state) => ({
    ...state,
    configLoading: false,
})));

const selectConfigState = () => createFeatureSelector(CONFIG_FEATURE_KEY);
const selectConfig = () => createSelector(selectConfigState(), (state) => state.config);
const selectConfigProperty = (key) => createSelector(selectConfig(), (config) => config ? config[key] : undefined);
const selectDomainId = createSelector(selectConfigState(), (state) => state.domainId);
const selectConfigLoading = createSelector(selectConfigState(), (state) => state.configLoading);
const selectConfigLoaded = createSelector(selectConfigState(), (state) => state.configLoaded);
const selectFeatureEnabled = (key) => createSelector(selectConfigProperty('features'), (features) => (features && Boolean(features[key])) ?? false);

var config_selectors = /*#__PURE__*/Object.freeze({
    __proto__: null,
    selectConfigState: selectConfigState,
    selectConfig: selectConfig,
    selectConfigProperty: selectConfigProperty,
    selectDomainId: selectDomainId,
    selectConfigLoading: selectConfigLoading,
    selectConfigLoaded: selectConfigLoaded,
    selectFeatureEnabled: selectFeatureEnabled
});

class ConfigEffects {
    constructor(domainId, configService, actions$, router, store, themeService) {
        this.domainId = domainId;
        this.configService = configService;
        this.actions$ = actions$;
        this.router = router;
        this.store = store;
        this.themeService = themeService;
        this.init$ = createEffect(() => this.actions$.pipe(ofType(init), mapToLatestFrom(this.store.select(selectDomainId)), filter((domainId) => domainId !== this.domainId), map(() => getConfig({ domainId: this.domainId }))));
        this.getConfig$ = createEffect(() => this.actions$.pipe(ofType(getConfig), switchMap((action) => this.configService.getConfig().pipe(map((config) => getConfigSuccess({
            config,
            domainId: action.domainId,
        })), catchError((error) => of(getConfigFailure({ error })))))));
        this.getConfigFailure$ = createEffect(() => this.actions$.pipe(ofType(getConfigFailure), tap(() => this.router.navigate(['/']))));
        this.domainTheme$ = createEffect(() => this.store.select(selectConfigProperty('themeCssClass')).pipe(filterNullUndefined(), tap((themeCssClass) => this.themeService.applyDomainTheme(themeCssClass))), { dispatch: false });
    }
    ngrxOnInitEffects() {
        return init();
    }
}
ConfigEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ConfigEffects, deps: [{ token: DOMAIN_ID }, { token: ConfigService }, { token: i2.Actions }, { token: i3.Router }, { token: i2$1.Store }, { token: ThemeService }], target: i0.ɵɵFactoryTarget.Injectable });
ConfigEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ConfigEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ConfigEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i6.DomainId, decorators: [{
                    type: Inject,
                    args: [DOMAIN_ID]
                }] }, { type: ConfigService }, { type: i2.Actions }, { type: i3.Router }, { type: i2$1.Store }, { type: ThemeService }]; } });

let ConfigFacade = class ConfigFacade {
    constructor(configService, store) {
        this.configService = configService;
        this.store = store;
        this.configSubject = new BehaviorSubject(this.configService.defaultConfig);
        this.config$ = this.configSubject;
        this.configLoaded$ = this.store
            .select(selectConfigLoaded)
            .pipe(filter((configLoaded) => configLoaded === true));
        this.configLoading$ = this.store.select(selectConfigLoading);
        this.connectToStore();
    }
    featureEnabled(feature) {
        const features = this.config.features;
        return Boolean(features[feature]);
    }
    selectConfigProperty(key) {
        return this.store.select(selectConfigProperty(key));
    }
    selectFeatureEnabled(feature) {
        return this.store.select(selectFeatureEnabled(feature));
    }
    get config() {
        return this.configSubject.value;
    }
    connectToStore() {
        this.store
            .select(selectConfigState())
            .pipe(filterNullUndefined(), map((state) => state.config), filterNullUndefined(), untilDestroyed(this))
            .subscribe((config) => {
            this.configSubject.next(config);
        });
    }
};
ConfigFacade.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ConfigFacade, deps: [{ token: ConfigService }, { token: i2$1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ConfigFacade.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ConfigFacade });
ConfigFacade = __decorate([
    UntilDestroy(),
    __metadata("design:paramtypes", [ConfigService, Store])
], ConfigFacade);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ConfigFacade, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: ConfigService }, { type: i2$1.Store }]; } });

class OptimizelyFeatureFlagConfigAdapter {
    constructor(domainConfig, environment, runtimeConfig, window, loggingService) {
        this.domainConfig = domainConfig;
        this.environment = environment;
        this.runtimeConfig = runtimeConfig;
        this.window = window;
        this.loggingService = loggingService;
        this.optimizelyConfig = this.window.optimizelyConfig ?? {};
    }
    getConfig() {
        const buildFeatures = this.buildFeatures;
        const domainFeatures = this.domainFeatures;
        const runtimeFeatures = this.runtimeFeatures;
        return {
            buildFeatures,
            domainFeatures,
            runtimeFeatures,
            features: {
                ...buildFeatures,
                ...runtimeFeatures,
                ...domainFeatures,
            },
        };
    }
    get buildFeatures() {
        return Object.keys(this.environment.buildFeatures)
            .map((buildFeature) => buildFeature)
            .reduce((buildFeatures, buildFeature) => {
            if (buildFeature in this.optimizelyConfig) {
                this.loggingService.debug('OptimizelyFeatureFlagConfigAdapter', `Overriding build feature ${buildFeature}.`);
                return {
                    ...buildFeatures,
                    [buildFeature]: this.optimizelyConfig[buildFeature],
                };
            }
            return buildFeatures;
        }, this.environment.buildFeatures);
    }
    get domainFeatures() {
        return Object.keys(this.domainConfig.domainFeatures)
            .map((domainFeature) => domainFeature)
            .reduce((domainFeatures, domainFeature) => {
            if (domainFeature in this.optimizelyConfig) {
                this.loggingService.debug('OptimizelyFeatureFlagConfigAdapter', `Overriding domain feature ${domainFeature}.`);
                return {
                    ...domainFeatures,
                    [domainFeature]: this.optimizelyConfig[domainFeature],
                };
            }
            return domainFeatures;
        }, this.domainConfig.domainFeatures);
    }
    get runtimeFeatures() {
        return Object.keys(this.runtimeConfig.runtimeFeatures)
            .map((runtimeFeature) => runtimeFeature)
            .reduce((runtimeFeatures, runtimeFeature) => {
            if (runtimeFeature in this.optimizelyConfig) {
                this.loggingService.debug('OptimizelyFeatureFlagConfigAdapter', `Overriding runtime feature ${runtimeFeature}.`);
                return {
                    ...runtimeFeatures,
                    [runtimeFeature]: this.optimizelyConfig[runtimeFeature],
                };
            }
            return runtimeFeatures;
        }, this.runtimeConfig.runtimeFeatures);
    }
}
OptimizelyFeatureFlagConfigAdapter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: OptimizelyFeatureFlagConfigAdapter, deps: [{ token: DOMAIN_CONFIG }, { token: ENVIRONMENT }, { token: RUNTIME_CONFIG }, { token: WINDOW }, { token: i1.LoggingService }], target: i0.ɵɵFactoryTarget.Injectable });
OptimizelyFeatureFlagConfigAdapter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: OptimizelyFeatureFlagConfigAdapter });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: OptimizelyFeatureFlagConfigAdapter, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOMAIN_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ENVIRONMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [RUNTIME_CONFIG]
                }] }, { type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: i1.LoggingService }]; } });

let FeatureEnabledDirective = class FeatureEnabledDirective {
    constructor(changeDetectorRef, configFacade, templateRef, viewContainer) {
        this.changeDetectorRef = changeDetectorRef;
        this.configFacade = configFacade;
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.additionalConditionSubject = new BehaviorSubject(true);
        this.featureSubject = new BehaviorSubject(undefined);
        this.featureEnabled$ = this.featureSubject.pipe(filterNullUndefined(), switchMap((feature) => this.configFacade.selectFeatureEnabled(feature)));
    }
    set dgxFeatureEnabled(feature) {
        this.featureSubject.next(feature);
    }
    set dgxFeatureEnabledAnd(additionalCondition) {
        this.additionalConditionSubject.next(Boolean(additionalCondition));
    }
    ngOnInit() {
        this.determineVisibility();
    }
    determineVisibility() {
        this.viewContainer.clear();
        combineLatest([this.additionalConditionSubject, this.featureEnabled$])
            .pipe(map((conditions) => conditions.every(Boolean)), distinctUntilChanged(), untilDestroyed(this))
            .subscribe((featureEnabled) => {
            this.viewContainer.clear();
            if (featureEnabled) {
                this.viewContainer.createEmbeddedView(this.templateRef);
            }
            else if (this.dgxFeatureEnabledElse) {
                this.viewContainer.createEmbeddedView(this.dgxFeatureEnabledElse);
            }
            this.changeDetectorRef.detectChanges();
        });
    }
};
FeatureEnabledDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: FeatureEnabledDirective, deps: [{ token: i0.ChangeDetectorRef }, { token: ConfigFacade }, { token: i0.TemplateRef }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive });
FeatureEnabledDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: FeatureEnabledDirective, selector: "[dgxFeatureEnabled]", inputs: { dgxFeatureEnabled: "dgxFeatureEnabled", dgxFeatureEnabledAnd: "dgxFeatureEnabledAnd", dgxFeatureEnabledElse: "dgxFeatureEnabledElse" }, ngImport: i0 });
FeatureEnabledDirective = __decorate([
    UntilDestroy(),
    __metadata("design:paramtypes", [ChangeDetectorRef,
        ConfigFacade,
        TemplateRef,
        ViewContainerRef])
], FeatureEnabledDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: FeatureEnabledDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dgxFeatureEnabled]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: ConfigFacade }, { type: i0.TemplateRef }, { type: i0.ViewContainerRef }]; }, propDecorators: { dgxFeatureEnabled: [{
                type: Input
            }], dgxFeatureEnabledAnd: [{
                type: Input
            }], dgxFeatureEnabledElse: [{
                type: Input
            }] } });

const configInitializer = (configFacade) => {
    return () => firstValueFrom(configFacade.configLoaded$.pipe(filter((configLoaded) => configLoaded)));
};

const configFactory = (configFacade) => configFacade.config;

class FeatureEnabledGuard {
    constructor(bypassRouteGuard, configFacade, loggingService, router) {
        this.bypassRouteGuard = bypassRouteGuard;
        this.configFacade = configFacade;
        this.loggingService = loggingService;
        this.router = router;
    }
    canActivate(route) {
        this.debug('Start');
        const redirectTo = route.data?.redirectTo ?? '/';
        const feature = route.data?.feature;
        if (this.bypassRouteGuard) {
            this.debug('Success');
            return of(true);
        }
        else if (!feature) {
            this.redirectTo(redirectTo);
            return of(false);
        }
        return this.configFacade.selectFeatureEnabled(feature).pipe(first(), tap((featureEnabled) => {
            featureEnabled ? this.debug('Success') : this.redirectTo(redirectTo);
        }));
    }
    redirectTo(redirectTo) {
        this.debug('Redirect', redirectTo);
        this.router.navigateByUrl(redirectTo);
    }
    debug(message, additionalMessage) {
        this.loggingService.debug('FeatureEnabledGuard', message, additionalMessage);
    }
}
FeatureEnabledGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: FeatureEnabledGuard, deps: [{ token: BYPASS_ROUTE_GUARD, optional: true }, { token: ConfigFacade }, { token: i1.LoggingService }, { token: i3.Router }], target: i0.ɵɵFactoryTarget.Injectable });
FeatureEnabledGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: FeatureEnabledGuard });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: FeatureEnabledGuard, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [BYPASS_ROUTE_GUARD]
                }] }, { type: ConfigFacade }, { type: i1.LoggingService }, { type: i3.Router }]; } });

const CONFIG = new InjectionToken('Config');

class DgxConfigModule {
    static forRoot() {
        return {
            ngModule: DgxConfigModule,
            providers: [
                ConfigFacade,
                ThemeService,
                FeatureEnabledGuard,
                {
                    provide: APP_INITIALIZER,
                    useFactory: configInitializer,
                    multi: true,
                    deps: [ConfigFacade],
                },
                {
                    provide: CONFIG,
                    useFactory: configFactory,
                    deps: [ConfigFacade],
                },
                {
                    provide: CONFIG_ADAPTER,
                    useClass: OptimizelyFeatureFlagConfigAdapter,
                    deps: [DOMAIN_CONFIG, ENVIRONMENT, RUNTIME_CONFIG, WINDOW, LoggingService],
                    multi: true,
                },
            ],
        };
    }
}
DgxConfigModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: DgxConfigModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DgxConfigModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: DgxConfigModule, declarations: [FeatureEnabledDirective], imports: [CommonModule,
        DgxLoggingModule, i2.EffectsFeatureModule, i2$1.StoreFeatureModule], exports: [FeatureEnabledDirective] });
DgxConfigModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: DgxConfigModule, providers: [], imports: [[
            CommonModule,
            DgxLoggingModule,
            EffectsModule.forFeature([ConfigEffects]),
            StoreModule.forFeature(CONFIG_FEATURE_KEY, configReducer),
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: DgxConfigModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        DgxLoggingModule,
                        EffectsModule.forFeature([ConfigEffects]),
                        StoreModule.forFeature(CONFIG_FEATURE_KEY, configReducer),
                    ],
                    providers: [],
                    declarations: [FeatureEnabledDirective],
                    exports: [FeatureEnabledDirective],
                }]
        }] });

const mockBuildFeatures = {};

const mockDomainIdMapping = {
    [DomainId.Beko]: /beko\.partners/,
};

const mockBuildConfig = {
    buildFeatures: mockBuildFeatures,
    defaultDomainId: DomainId.DomesticAndGeneral,
    domainIdMapping: mockDomainIdMapping,
    requestAction: RequestAction.QuoteAndBuy,
    trustpilot: mockTrustpilotConfig,
    whiteLabelClient: RequestSource.DomesticAndGeneralUK,
};

const mockDomainFeatures = {};

const mockDomainConfig = {
    catalogueStore: RequestSource.DomesticAndGeneralUK,
    clientBrandCode: undefined,
    clientName: 'clientName',
    domainFeatures: mockDomainFeatures,
    domainId: DomainId.DomesticAndGeneral,
};

const mockEnvironment = {
    ...mockBuildConfig,
    runtimeConfigPath: '',
    production: false,
};

const mockRuntimeFeatures = {};

const mockFeatures = {
    ...mockBuildFeatures,
    ...mockRuntimeFeatures,
    ...mockDomainFeatures,
};

const mockCspConfig = {
    apiUrlPattern: 'https://*.test.test.athome.domgentest.cloud',
};

const mockRuntimeConfig = {
    csp: mockCspConfig,
    runtimeFeatures: mockRuntimeFeatures,
};

const mockConfig = {
    ...mockEnvironment,
    ...mockRuntimeConfig,
    ...mockDomainConfig,
    features: mockFeatures,
};

const mockConfigState = {
    domainId: DomainId.DomesticAndGeneral,
    config: mockConfig,
    configLoaded: false,
    configLoading: true,
};

var BuildFeature;
(function (BuildFeature) {
})(BuildFeature || (BuildFeature = {}));

var DomainFeature;
(function (DomainFeature) {
})(DomainFeature || (DomainFeature = {}));

var RuntimeFeature;
(function (RuntimeFeature) {
})(RuntimeFeature || (RuntimeFeature = {}));

function domainIdFromDomain(defaultDomainId, domainIdMapping) {
    if (!domainIdMapping) {
        return defaultDomainId;
    }
    const url = new URL(window.location.href);
    const matchedDomainId = Object.entries(domainIdMapping)
        .map(([domainId, domainPattern]) => ({
        domainId: domainId,
        domainPattern,
    }))
        .find(({ domainPattern }) => domainPattern?.test(url.hostname))?.domainId;
    return matchedDomainId ?? defaultDomainId;
}

/**
 * Generated bundle index. Do not edit.
 */

export { BuildFeature, CONFIG, CONFIG_ADAPTER, CONFIG_FEATURE_KEY, config_actions as ConfigActions, ConfigEffects, ConfigFacade, config_selectors as ConfigSelectors, ConfigService, DOMAIN_CONFIG, DgxConfigModule, DomainFeature, ENVIRONMENT, FeatureEnabledDirective, FeatureEnabledGuard, RUNTIME_CONFIG, RuntimeFeature, ThemeService, configFactory, configInitializer, configReducer, domainIdFromDomain, initialState, mockBuildConfig, mockBuildFeatures, mockConfig, mockConfigState, mockCspConfig, mockDomainConfig, mockDomainFeatures, mockDomainIdMapping, mockEnvironment, mockFeatures, mockRuntimeConfig, mockRuntimeFeatures };
